import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Select, Input, Typography } from "antd";
import { FiPlus, FiSearch, FiUsers, FiZap, FiBriefcase, FiFileText, FiArrowLeft, FiCheck, FiBookmark } from "react-icons/fi";
import {
  setActiveConversation,
  fetchConversations,
  createConversation,
  fetchUserPresences,
  searchMessages,
} from "../../../Slices/chatSlice";
import { getInitials, getAvatarColor, getDisplayName } from "./avatarUtils";
import { getStatusDotClass, getStatusLabel, STATUS_OPTIONS } from "./presenceUtils";
import { SERVER_URL } from "../../../databucket";
import socket from "../Sales1/socket";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "./chatWidget.css";

const BOT_ID = 'ai-assistant-bot';

dayjs.extend(relativeTime);

const { Text } = Typography;

const ChatSidebar = ({ mode = "fullscreen", onConversationSelect }) => {
  const dispatch = useDispatch();
  const conversations = useSelector((state) => state.chat.conversations);
  const activeConversationId = useSelector((state) => state.chat.activeConversationId);
  const conversationsLoading = useSelector((state) => state.chat.conversationsLoading);
  const onlineUsers = useSelector((state) => state.chat.onlineUsers);
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const allUsers = useSelector((state) => state?.loginUserInfo?.allUsers);

  const [search, setSearch] = useState("");
  const [msgSearchResults, setMsgSearchResults] = useState([]);
  const [msgSearchLoading, setMsgSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [customStatusText, setCustomStatusText] = useState("");
  const botInitRef = useRef(false);

  useEffect(() => {
    if (loginUser?.id) {
      dispatch(fetchConversations({ userId: loginUser.id }));
    }
  }, [loginUser?.id, dispatch]);

  // Auto-create bot conversation if it doesn't exist
  useEffect(() => {
    if (!loginUser?.id || botInitRef.current || conversationsLoading) return;
    const hasBotConv = conversations.some(
      (c) => c.category === 'ai-bot' || c.participants?.some((p) => p.userId === BOT_ID)
    );
    if (!hasBotConv && conversations.length > 0) {
      botInitRef.current = true;
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      fetch(`${SERVER_URL}/api/bot/ensure-conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId: loginUser.id, userName: `${loginUser.firstname || ''} ${loginUser.lastname || ''}`.trim() }),
      })
        .then((res) => res.json())
        .then(() => {
          dispatch(fetchConversations({ userId: loginUser.id }));
        })
        .catch((err) => {
          console.error('[BotInit] Error creating bot conversation:', err);
        });
    }
  }, [loginUser?.id, conversations, conversationsLoading, dispatch]);

  useEffect(() => {
    const allParticipantIds = new Set();
    conversations.forEach((conv) => {
      conv.participants?.forEach((p) => {
        if (p.userId !== loginUser?.id) allParticipantIds.add(p.userId);
      });
    });
    if (allParticipantIds.size > 0) {
      dispatch(fetchUserPresences([...allParticipantIds]));
    }
  }, [conversations, loginUser?.id, dispatch]);

  // Map conversationId → matching message snippet (built from global message search)
  // so conversations with hits in OLDER messages also surface in the main list,
  // not just ones where the name or lastMessage matches.
  const matchedMsgByConv = useMemo(() => {
    const map = new Map();
    for (const m of msgSearchResults) {
      if (!map.has(m.conversationId)) {
        map.set(m.conversationId, m);
      }
    }
    return map;
  }, [msgSearchResults]);

  const filtered = useMemo(() => {
    let list = conversations;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((conv) => {
        const name = getDisplayName(conv, loginUser?.id, allUsers)?.toLowerCase() || "";
        const lastMsg = conv.lastMessage?.text?.toLowerCase() || "";
        // Match by conversation name, by lastMessage text, OR by any older message
        // surfaced via the global searchMessages thunk.
        return (
          name.includes(q) ||
          lastMsg.includes(q) ||
          matchedMsgByConv.has(conv.id)
        );
      });
    }
    return list;
  }, [conversations, search, loginUser?.id, allUsers, matchedMsgByConv]);

  // Global message search (across all conversations)
  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    if (!search.trim() || search.trim().length < 2) {
      setMsgSearchResults([]);
      return;
    }
    setMsgSearchLoading(true);
    searchDebounceRef.current = setTimeout(() => {
      dispatch(searchMessages({ userId: loginUser?.id, query: search.trim(), limit: 20 }))
        .then((result) => {
          setMsgSearchResults(result.payload?.response || []);
          setMsgSearchLoading(false);
        })
        .catch(() => setMsgSearchLoading(false));
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search, loginUser?.id, dispatch]);

  // Detect self-chat: category === 'self' OR direct chat with only yourself as participant
  const isSelfConv = (c) =>
    c.category === 'self' ||
    (c.type === 'direct' && c.participants?.length === 1 && c.participants[0]?.userId === loginUser?.id);

  const isBotConv = (c) =>
    (c.category === 'ai-bot' || c.participants?.some((p) => p.userId === BOT_ID)) && !isSelfConv(c);

  // Split conversations into sections
  const selfChat = useMemo(() => filtered.find((c) => isSelfConv(c)), [filtered, loginUser?.id]);
  const botChats = useMemo(() => filtered.filter((c) => isBotConv(c)), [filtered, loginUser?.id]);
  const regularChats = useMemo(() => filtered.filter((c) => !isSelfConv(c) && !isBotConv(c)), [filtered, loginUser?.id]);

  const getLastMessagePreview = (conv) => {
    // When the conversation surfaces because an OLDER message matches the search
    // and neither the name nor the lastMessage contains the query, show the
    // matched message text in the preview instead of the lastMessage. This way
    // the user sees WHY the conversation matched.
    if (search.trim()) {
      const q = search.toLowerCase();
      const nameMatches = (getDisplayName(conv, loginUser?.id, allUsers) || "").toLowerCase().includes(q);
      const lastMsgMatches = (conv.lastMessage?.text || "").toLowerCase().includes(q);
      const matchedMsg = matchedMsgByConv.get(conv.id);
      if (matchedMsg && !nameMatches && !lastMsgMatches) {
        const prefix = matchedMsg.senderId === loginUser?.id ? "You: " : "";
        const text = matchedMsg.content || "";
        return prefix + (text.length > 35 ? text.substring(0, 35) + "..." : text);
      }
    }
    if (!conv.lastMessage?.text) return "No messages yet";
    const prefix = conv.lastMessage.senderId === loginUser?.id ? "You: " : "";
    const text = conv.lastMessage.text;
    return prefix + (text.length > 35 ? text.substring(0, 35) + "..." : text);
  };

  const getTimeDisplay = (conv) => {
    if (!conv.lastMessage?.sentAt) return "";
    return dayjs(conv.lastMessage.sentAt).fromNow();
  };

  const handleConversationClick = (convId) => {
    dispatch(setActiveConversation(convId));
    onConversationSelect?.(convId);
  };

  const handleCreateConversation = () => {
    if (selectedUsers.length === 0) return;
    const participantIds = [...selectedUsers, loginUser?.id];
    dispatch(
      createConversation({
        type: selectedUsers.length > 1 ? "group" : "direct",
        name: groupName || "",
        participantIds,
        createdBy: loginUser?.id,
      })
    );
    setShowNewChat(false);
    setSelectedUsers([]);
    setGroupName("");
    onConversationSelect?.("new");
  };

  const handleSelfChat = () => {
    dispatch(
      createConversation({
        type: "direct",
        name: "",
        participantIds: [loginUser?.id],
        createdBy: loginUser?.id,
      })
    );
    setShowNewChat(false);
    onConversationSelect?.("new");
  };

  const userOptions = useMemo(() => {
    if (!allUsers) return [];
    return allUsers
      .filter((u) => u.id !== loginUser?.id)
      .map((u) => ({
        label: u.firstname
          ? `${u.firstname} ${u.lastname || ""}`.trim()
          : u.email || u.id,
        value: u.id,
      }));
  }, [allUsers, loginUser?.id]);

  return (
    <div className={`chat-sidebar ${mode === "widget" ? "chat-sidebar--widget" : "chat-sidebar--fullscreen"}`}>
      {/* Header with status picker */}
      <div className="chat-sidebar__header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="chat-sidebar__title">Chat</span>
          {/* Current user status dot — click to open picker */}
          <span
            className={`chat-avatar__dot ${getStatusDotClass(onlineUsers[loginUser?.id]?.status || "online")}`}
            style={{ position: "relative", width: 10, height: 10, cursor: "pointer", border: "2px solid #fff", borderRadius: "50%" }}
            onClick={() => setShowStatusPicker(!showStatusPicker)}
            title={`Your status: ${getStatusLabel(onlineUsers[loginUser?.id]?.status || "online", onlineUsers[loginUser?.id]?.customStatus)}`}
          />
        </div>
        <button
          className="chat-sidebar__new-btn"
          onClick={() => setShowNewChat(true)}
          title="New conversation"
        >
          <FiPlus />
        </button>
      </div>

      {/* Status Picker Dropdown */}
      {showStatusPicker && (
        <div style={{
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 8, margin: "0 12px 8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 10,
        }}>
          {STATUS_OPTIONS.map((opt) => (
            <div
              key={opt.value}
              onClick={() => {
                socket.emit("chat:set-status", { userId: loginUser?.id, status: opt.value, customStatus: "" });
                setShowStatusPicker(false);
                setCustomStatusText("");
              }}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6,
                cursor: "pointer", fontSize: 13,
                background: (onlineUsers[loginUser?.id]?.status || "online") === opt.value ? "#f0f7ff" : "transparent",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={(e) => e.currentTarget.style.background = (onlineUsers[loginUser?.id]?.status || "online") === opt.value ? "#f0f7ff" : "transparent"}
            >
              <span style={{ color: opt.color, fontSize: 10 }}>{opt.icon}</span>
              <span style={{ color: "#374151" }}>{opt.label}</span>
            </div>
          ))}
          <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 6, paddingTop: 6 }}>
            <input
              type="text"
              placeholder="Set custom status..."
              value={customStatusText}
              onChange={(e) => setCustomStatusText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && customStatusText.trim()) {
                  socket.emit("chat:set-status", { userId: loginUser?.id, status: onlineUsers[loginUser?.id]?.status || "online", customStatus: customStatusText.trim() });
                  setShowStatusPicker(false);
                  setCustomStatusText("");
                }
              }}
              style={{
                width: "100%", border: "1px solid #e5e7eb", borderRadius: 6, padding: "5px 8px",
                fontSize: 12, outline: "none",
              }}
            />
          </div>
        </div>
      )}

      {/* Search */}
      <div className="chat-sidebar__search" style={{ position: "relative" }}>
        <FiSearch className="chat-sidebar__search-icon" />
        <input
          placeholder="Search names or messages..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Conversation List */}
      <div className="chat-sidebar__list">
        {conversationsLoading && conversations.length === 0 && (
          <div className="chat-sidebar__empty">Loading...</div>
        )}
        {!conversationsLoading && filtered.length === 0 && (
          <div className="chat-sidebar__empty">No conversations yet</div>
        )}

        {/* Self-Chat Section */}
        {selfChat && (
          <>
            <div className="chat-sidebar__section-header">
              <FiBookmark size={12} />
              <span>Personal</span>
            </div>
            <div
              className={`chat-conv-item ${selfChat.id === activeConversationId ? "chat-conv-item--active" : ""}`}
              onClick={() => handleConversationClick(selfChat.id)}
            >
              <div className="chat-avatar chat-avatar--md chat-avatar--self">
                <FiBookmark size={16} />
                <span className="chat-avatar__dot chat-avatar__dot--online" />
              </div>
              <div className="chat-conv-item__body">
                <div className="chat-conv-item__row">
                  <span className="chat-conv-item__name">Notes to Self</span>
                  <span className="chat-conv-item__time">{getTimeDisplay(selfChat)}</span>
                </div>
                <div className="chat-conv-item__row">
                  <span className="chat-conv-item__preview">{getLastMessagePreview(selfChat)}</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Bot Section */}
        {botChats.length > 0 && (
          <>
            <div className="chat-sidebar__section-header">
              <FiZap size={12} />
              <span>AI Assistant</span>
            </div>
            {botChats.map((conv) => (
              <div
                key={conv.id}
                className={`chat-conv-item chat-conv-item--bot ${conv.id === activeConversationId ? "chat-conv-item--active" : ""}`}
                onClick={() => handleConversationClick(conv.id)}
              >
                <div className="chat-avatar chat-avatar--md chat-avatar--bot">
                  <FiZap size={16} />
                </div>
                <div className="chat-conv-item__body">
                  <div className="chat-conv-item__row">
                    <span className="chat-conv-item__name">AI Assistant</span>
                    <span className="chat-conv-item__time">{getTimeDisplay(conv)}</span>
                  </div>
                  <div className="chat-conv-item__row">
                    <span className="chat-conv-item__preview">{getLastMessagePreview(conv)}</span>
                    {conv.unreadCount > 0 && (
                      <span className="chat-conv-item__unread">{conv.unreadCount}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Chats Section */}
        {regularChats.length > 0 && (
          <div className="chat-sidebar__section-header">
            <FiUsers size={12} />
            <span>Chats</span>
          </div>
        )}
        {regularChats.map((conv) => {
          const isActive = conv.id === activeConversationId;
          const otherUserId =
            conv.type === "direct"
              ? conv.participants?.find((p) => p.userId !== loginUser?.id)?.userId
              : null;
          const userPresence = otherUserId ? onlineUsers[otherUserId] : null;
          const presenceStatus = userPresence?.status || "offline";
          const otherUser = otherUserId
            ? allUsers?.find((u) => u.id === otherUserId)
            : null;
          const name = getDisplayName(conv, loginUser?.id, allUsers);
          const hasEntity = conv.linkedEntity?.entityType && conv.linkedEntity?.entityId;

          return (
            <div
              key={conv.id}
              className={`chat-conv-item ${isActive ? "chat-conv-item--active" : ""}`}
              onClick={() => handleConversationClick(conv.id)}
            >
              {conv.type === "group" ? (
                <div
                  className="chat-avatar chat-avatar--md"
                  style={{ background: "var(--chat-primary)" }}
                >
                  <FiUsers size={16} />
                </div>
              ) : (
                <div
                  className="chat-avatar chat-avatar--md"
                  style={{ background: getAvatarColor(otherUserId) }}
                >
                  {getInitials(
                    otherUser?.firstname || name?.split(" ")[0],
                    otherUser?.lastname || name?.split(" ")[1]
                  )}
                  <span
                    className={`chat-avatar__dot ${getStatusDotClass(presenceStatus)}`}
                    title={getStatusLabel(presenceStatus, userPresence?.customStatus, userPresence?.lastSeen)}
                  />
                </div>
              )}
              <div className="chat-conv-item__body">
                <div className="chat-conv-item__row">
                  <span className="chat-conv-item__name">{name}</span>
                  <span className="chat-conv-item__time">{getTimeDisplay(conv)}</span>
                </div>
                <div className="chat-conv-item__row">
                  <span className="chat-conv-item__preview">{getLastMessagePreview(conv)}</span>
                  {conv.unreadCount > 0 && (
                    <span className="chat-conv-item__unread">{conv.unreadCount}</span>
                  )}
                </div>
                {hasEntity && (
                  <div className="chat-conv-item__entity">
                    {conv.linkedEntity.entityType === 'lead' ? <FiBriefcase size={10} /> : <FiFileText size={10} />}
                    <span>{conv.linkedEntity.entityName || conv.linkedEntity.entityId}</span>
                    {conv.linkedEntity.entityMeta?.status && (
                      <span className="chat-conv-item__entity-status">{conv.linkedEntity.entityMeta.status}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Global Message Search Results */}
        {search.trim().length >= 2 && (
          <>
            <div className="chat-sidebar__section-header" style={{ marginTop: 8 }}>
              <FiSearch size={12} />
              <span>Messages {msgSearchLoading ? "..." : `(${msgSearchResults.length})`}</span>
            </div>
            {msgSearchResults.length === 0 && !msgSearchLoading && (
              <div className="chat-sidebar__empty" style={{ fontSize: 12, padding: "8px 16px" }}>
                No messages found
              </div>
            )}
            {msgSearchResults.map((msg) => {
              const conv = conversations.find((c) => c.id === msg.conversationId);
              const convName = conv ? getDisplayName(conv, loginUser?.id, allUsers) : "Unknown";
              return (
                <div
                  key={msg.id}
                  className={`chat-conv-item ${msg.conversationId === activeConversationId ? "chat-conv-item--active" : ""}`}
                  onClick={() => {
                    handleConversationClick(msg.conversationId);
                    setSearch("");
                    setMsgSearchResults([]);
                  }}
                  style={{ padding: "8px 16px" }}
                >
                  <div
                    className="chat-avatar chat-avatar--sm"
                    style={{ background: getAvatarColor(msg.senderId), flexShrink: 0 }}
                  >
                    {getInitials(msg.senderName?.split(" ")[0], msg.senderName?.split(" ")[1])}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--chat-text)" }}>{msg.senderName}</span>
                      <span style={{ fontSize: 10, color: "var(--chat-text-muted)" }}>
                        {dayjs(msg.createdAt).format("MMM D")}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--chat-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {msg.content?.length > 60 ? msg.content.slice(0, 60) + "..." : msg.content}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--chat-text-muted)", marginTop: 1 }}>
                      in {convName}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* New Chat — Inline Panel */}
      {showNewChat && (
        <div className="new-chat-panel">
          <div className="new-chat-panel__header">
            <button
              className="chat-header__back"
              onClick={() => { setShowNewChat(false); setSelectedUsers([]); setGroupName(""); }}
              type="button"
            >
              <FiArrowLeft size={16} />
            </button>
            <span className="new-chat-panel__title">New Conversation</span>
            <button
              className="chat-header__action-btn"
              onClick={handleCreateConversation}
              disabled={selectedUsers.length === 0}
              title="Start Chat"
              type="button"
              style={selectedUsers.length > 0 ? { background: "var(--chat-primary)", color: "#fff" } : {}}
            >
              <FiCheck size={16} />
            </button>
          </div>
          <div className="new-chat-panel__body">
            <button
              className="chat-self-chat-btn"
              onClick={handleSelfChat}
              type="button"
            >
              <div className="chat-avatar chat-avatar--sm" style={{ background: '#6366f1' }}>
                <FiBookmark size={14} />
              </div>
              <div className="chat-self-chat-btn__text">
                <span className="chat-self-chat-btn__title">Notes to Self</span>
                <span className="chat-self-chat-btn__subtitle">Save messages, links & reminders</span>
              </div>
            </button>
            <div style={{ borderBottom: '1px solid var(--chat-border)', margin: '8px 0' }} />
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Select people
              </Text>
              <Select
                mode="multiple"
                placeholder="Search team members..."
                value={selectedUsers}
                onChange={setSelectedUsers}
                options={userOptions}
                style={{ width: "100%", marginTop: 4 }}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                autoFocus
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
            {selectedUsers.length > 1 && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Group Name (optional)
                </Text>
                <Input
                  placeholder="Enter group name..."
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  style={{ marginTop: 4 }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatSidebar;
